import { useContext, useState } from 'react';
import styles from './Row.module.css';
import AppContext from '../../AppContext';
import RatioRow from './AnnualChangeRow/RatioRow';
import AmountRow from './AnnualChangeRow/AmountRow';
import TextRow from './AnnualChangeRow/TextRow';
import LabelRow from './LabelRow';
import LinkRow from './AnnualChangeRow/LinkRow';
import classNames from 'classnames/bind';
import AnnualChangeRowData from './AnnualChangeRow/AnnualChangeRowData';
import RowHelmet from './RowHelmet';
import BlankRow from './BlankRow/BlankRow';
import IrisRow from './AnnualChangeRow/IRISRow';

const cx = classNames.bind(styles);

interface RowBuilderProps {
  data: AnnualChangeRowData;
  showDollarChange?: boolean;
  showPercentPointChange?: boolean;
  sectionKey: string;
}

function RowBuilder({
  data,
  showDollarChange,
  showPercentPointChange,
  sectionKey
}: RowBuilderProps) {
  const { showBenchmark, showIndustryMean, riskRepository, activeYearsMode } =
    useContext(AppContext);
  const [isVizExpanded, setIsVizExpanded] = useState(false);
  const yearColsStyle = { '--year-cols': activeYearsMode === 'TEN' ? 10 : 5 } as React.CSSProperties;

  const hasTableauViz = !!data?.embeddedDashboardUrl?.trim();

  function handleLabelClick() {
    if (hasTableauViz) {
      setIsVizExpanded((prev) => !prev);
    }
  }

  function populateClassNameArray() {
    let classNameArray = [];

    classNameArray.push('container');

    if (!showBenchmark) {
      classNameArray.push('collapse');
    }

    if (!showIndustryMean) {
      classNameArray.push('hideIndustryMean');
    }

    if (getDataDisplayForCurrentRepository(data)) {
      classNameArray.push(getDataDisplayForCurrentRepository(data));
    }

    if (getRowStyle(data)) {
      classNameArray.push(getRowStyle(data));
    }

    if (data.rowType === 'BLANK') {
      classNameArray.push('blank_row');
    }

    return classNameArray;
  }

  function getRowStyle(rowData: AnnualChangeRowData) {
    switch (rowData.rowClass) {
      case 'TOTAL':
        return styles.total_border;
      case 'INDENT':
        return styles.indent;
      case 'DOUBLE_INDENT':
        return styles.double_indent;
      case 'GRAND_TOTAL':
        return styles.grand_total_border;
      case 'BOLD':
        return styles.bold;
      default:
        return '';
    }
  }

  const className = cx(populateClassNameArray());

  function getDataDisplayForCurrentRepository(data: AnnualChangeRowData) {
    let { rowDisplay } = data;

    if (riskRepository === 'FR' || !rowDisplay) {
      return '';
    }

    if (riskRepository) {
      if (rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay] === 'GREY') {
        return 'grey';
      } else if (rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay] === 'HIDE') {
        return 'hidden';
      } else {
        return '';
      }
    }
  }

  function hasAnyOutsideBenchmarks(data: AnnualChangeRowData) {
    return (
      data.percentOrPointChange?.outsideBenchmark ||
      data.dollarChange?.outsideBenchmark ||
      data.percentOfPHS?.outsideBenchmark ||
      data.percentOfCAS?.outsideBenchmark ||
      data.percentOfNAA?.outsideBenchmark ||
      data.percentOfTDP?.outsideBenchmark ||
      data.currentYear?.outsideBenchmark ||
      data.priorYear?.outsideBenchmark ||
      data.priorYearMinusOne?.outsideBenchmark ||
      data.priorYearMinusTwo?.outsideBenchmark ||
      data.priorYearMinusThree?.outsideBenchmark ||
      data.priorYearMinusFour?.outsideBenchmark ||
      data.priorYearMinusFive?.outsideBenchmark ||
      data.priorYearMinusSix?.outsideBenchmark ||
      data.priorYearMinusSeven?.outsideBenchmark ||
      data.priorYearMinusEight?.outsideBenchmark
    );
  }

  function populateRowByType() {
    if (data.rowType === 'IRIS_RATIO') {
      return <IrisRow data={data}></IrisRow>;
    } else if (data.rowType === 'RATIO' || data.rowType === 'ABSOLUTE_RATIO') {
      return (
        <RatioRow
          data={data}
          showPercentPointChange={showPercentPointChange}
          isVizExpanded={isVizExpanded}
        />
      );
    } else if (data.rowType === 'AMOUNT') {
      return (
        <AmountRow
          data={data}
          showPercentPointChange={showPercentPointChange}
          showDollarChange={showDollarChange}
        />
      );
    } else if (data.rowType === 'TEXT') {
      return <TextRow {...data} />;
    } else if (data.rowType === 'LINK') {
      return <LinkRow {...data} isVizExpanded={isVizExpanded} />;
    } else {
      return;
    }
  }

  function getRowId(sectionKey: string, data: AnnualChangeRowData) {
    if (data.scoringStatementType) {
      return `${sectionKey}-${data.rowKey}-${data.scoringStatementType}`;
    } else {
      return `${sectionKey}-${data.rowKey}`;
    }
  }

  if (data) {
    if (data.rowType === 'BLANK') {
      return <BlankRow />;
    } else if (data.rowType === 'LABEL') {
      return (
        <div
          id={
            'label-' +
            data.label
              .replace(/\s+/g, '-')
              .replace(/[^a-z^A-Z^0-9^-]+/g, '')
              .toLowerCase()
          }
          className={className}
          style={yearColsStyle}>
          <RowHelmet label={data.label} rowLinkConfiguration={data.rowLinkConfiguration} />
          <LabelRow></LabelRow>
        </div>
      );
    } else {
      return (
        <div
          id={getRowId(sectionKey, data)}
          className={className}
          style={yearColsStyle}
          data-testid="data-row">
          <RowHelmet
            label={data.label}
            rowKey={data.rowKey}
            isOutsideBenchmark={hasAnyOutsideBenchmarks(data)}
            benchmarkText={data.benchmark}
            calculateDetail={data.calculateDetail}
            rowType={data.rowType}
            rowDataSource={data.rowDataSource}
            rowLinkConfiguration={data.rowLinkConfiguration}
            sectionKey={sectionKey}
            onLabelClick={hasTableauViz ? handleLabelClick : undefined}
            isCollapsible={hasTableauViz}
            isExpanded={isVizExpanded}
          />
          {populateRowByType()}
        </div>
      );
    }
  } else return null;
}

export default RowBuilder;
